import React from 'react'

import {
    Box,
    Paper,
    List,
    ListItemIcon,
    ListItemText,
    styled,
    ListItemButton,
    ListItemButtonProps,
} from '@mui/material'
import { DirectionsCar as DirectionsCarIcon, Domain as DomainIcon, Person as PersonIcon } from '@mui/icons-material'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { useGlobalSearchQuery } from '../../../../generated/graphql'

import getSingleResultUrl from './getSingleResultUrl'
import QueryLanguageHelper from '../QueryLanguageHelper'
import isHelpString from '../QueryLanguageHelper/isHelpString'
import { useIsGlobalAdmin } from '@edge/spark/io-roles'
import isQueryTag from '../QueryLanguageHelper/isQueryTag'

const StyledListItemIcon = styled(ListItemIcon)`
    min-width: 30px;
`

const StyledListItemButton = styled(ListItemButton)`
    padding: 0px 8px;
    ${({ className }) => (className === 'active' ? 'background-color: #ddd;' : '')}
`

interface Props extends RouteComponentProps {
    searchString: string
    mobile?: boolean
    selectedFilter: FilterType
    updateSingleResult: (newString: string | null) => void
}

const SearchBarResultsMenu = ({
    searchString,
    mobile,
    history,
    selectedFilter,
    updateSingleResult,
}: Props): React.ReactElement => {
    const handleClick: ListItemButtonProps['onClick'] = (e) => {
        history.push(`/search?query=${searchString}&filter=${e.currentTarget.id}`)
    }

    const { data, loading } = useGlobalSearchQuery({
        variables: { searchString },
        fetchPolicy: 'network-only',
    })

    const { vehicleCount, vehicleRecentlySoldCount, vehicleInSaleCount, accountCount, agentCount } =
        data?.auction?.search || {}

    React.useEffect(() => {
        if (data) {
            updateSingleResult(getSingleResultUrl(data))
        }
    }, [data, updateSingleResult])

    const formatResults = (count: number | null | undefined): string => {
        if (loading) {
            return '(Loading...)'
        }

        if (count === null || count === undefined || count === 0) {
            return '(No results)'
        }

        if (count > 999) {
            return `(${Math.floor(count / 1000)}k)`
        }

        return `(${count.toString()})`
    }

    const isGlobalAdmin = useIsGlobalAdmin()
    const isSearchHelpString = isQueryTag(searchString) || isHelpString(searchString)

    return (
        <Box sx={{ position: 'relative' }}>
            <Paper
                sx={{ position: 'absolute', width: '100%', zIndex: 999 }}
                elevation={12}
                style={
                    isSearchHelpString
                        ? {
                              maxHeight: '60vh',
                              overflow: 'scroll',
                              width: mobile ? '80vw' : undefined,
                          }
                        : {}
                }
            >
                <List component="nav" dense={!mobile}>
                    {isGlobalAdmin && isSearchHelpString ? (
                        <QueryLanguageHelper searchString={searchString} />
                    ) : (
                        <>
                            <StyledListItemButton
                                id="ALL_VEHICLES"
                                className={selectedFilter === 'ALL_VEHICLES' ? 'active' : undefined}
                                onClick={handleClick}
                            >
                                <StyledListItemIcon>
                                    <DirectionsCarIcon />
                                </StyledListItemIcon>
                                <ListItemText>Inventory {formatResults(vehicleCount)}</ListItemText>
                            </StyledListItemButton>
                            <StyledListItemButton
                                id="VEHICLES_IN_SALE"
                                className={selectedFilter === 'VEHICLES_IN_SALE' ? 'active' : undefined}
                                onClick={handleClick}
                            >
                                <ListItemText inset>In Sale {formatResults(vehicleInSaleCount)}</ListItemText>
                            </StyledListItemButton>
                            <StyledListItemButton
                                id="VEHICLES_SOLD"
                                className={selectedFilter === 'VEHICLES_SOLD' ? 'active' : undefined}
                                onClick={handleClick}
                            >
                                <ListItemText inset>
                                    Recently Sold {formatResults(vehicleRecentlySoldCount)}
                                </ListItemText>
                            </StyledListItemButton>

                            <StyledListItemButton
                                id="COMPANIES"
                                className={selectedFilter === 'COMPANIES' ? 'active' : undefined}
                                onClick={handleClick}
                            >
                                <StyledListItemIcon>
                                    <DomainIcon />
                                </StyledListItemIcon>
                                <ListItemText>Companies {formatResults(accountCount)}</ListItemText>
                            </StyledListItemButton>
                            <StyledListItemButton
                                id="AGENTS"
                                className={selectedFilter === 'AGENTS' ? 'active' : undefined}
                                onClick={handleClick}
                            >
                                <StyledListItemIcon>
                                    <PersonIcon />
                                </StyledListItemIcon>
                                <ListItemText data-test="agentMenuL">Agents {formatResults(agentCount)}</ListItemText>
                            </StyledListItemButton>
                        </>
                    )}
                </List>
            </Paper>
        </Box>
    )
}

export default withRouter(SearchBarResultsMenu)
